import { jsPDF } from 'jspdf';
import { svg2pdf } from 'svg2pdf.js';
import { getJsPdfPageFormat } from '../utils/pdfPageSize';
import { loadSceneFontVariants } from '../utils/googleFonts';
import { renderSceneToSvgElement, renderSceneToSvgString } from '../renderers/svgSceneRenderer';
import { fabricCanvasToSceneGraph } from '../sceneGraph/fabricAdapter';
import { migrateFieldConfigToConstraints } from '../sceneGraph/fieldConfigMigration';
import { layoutSceneGraph, layoutTextNode } from '../typography/layoutText';
import { createMeasureFn, clearMeasureCache } from '../typography/measureText';

// Fonts that jsPDF supports natively — no embedding needed
const NATIVE_PDF_FONTS = new Set([
  'helvetica', 'arial', 'times', 'times new roman', 'courier', 'courier new',
  'symbol', 'zapfdingbats',
]);

function isNativePdfFont(family) {
  return NATIVE_PDF_FONTS.has(String(family || '').toLowerCase().trim());
}

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/** User-Agent that makes Google Fonts CSS return TTF (required by jsPDF.addFont). */
const GOOGLE_FONTS_TTF_UA =
  'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.1; Trident/6.0)';

/**
 * Fetch Google Font CSS for the given family/weight/style and extract a TTF/OTF URL.
 */
async function fetchGoogleFontUrl(family, weight = '400', style = 'normal') {
  const q = `${family.replace(/ /g, '+')}:ital,wght@${style === 'italic' ? 1 : 0},${weight}`;
  const css = await fetch(`https://fonts.googleapis.com/css2?family=${q}&display=swap`, {
    headers: { 'User-Agent': GOOGLE_FONTS_TTF_UA },
  })
    .then((r) => (r.ok ? r.text() : ''))
    .catch(() => '');
  const match = css.match(/url\((https?:\/\/[^)]+\.(?:ttf|otf))\)/i);
  return match ? match[1] : null;
}

/**
 * Register Google Fonts needed by the scene into a jsPDF instance so svg2pdf uses them.
 * Falls back silently for any font that cannot be fetched.
 */
async function embedFontsInPdf(pdf, scene) {
  const entries = [];
  for (const obj of scene?.objects || []) {
    if (obj.type !== 'text') continue;
    const family = obj.typography?.fontFamily;
    if (!family || isNativePdfFont(family)) continue;
    const weight = String(obj.typography?.fontWeight || 'normal');
    const style  = obj.typography?.fontStyle === 'italic' ? 'italic' : 'normal';
    const jsPdfStyle =
      style === 'italic'
        ? weight === 'bold' || weight === '700' ? 'bolditalic' : 'italic'
        : weight === 'bold' || weight === '700' ? 'bold' : 'normal';
    entries.push({ family, weight, style, jsPdfStyle });
  }

  const seen = new Set();
  const unique = entries.filter(({ family, jsPdfStyle }) => {
    const k = `${family}::${jsPdfStyle}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  await Promise.allSettled(
    unique.map(async ({ family, weight, style, jsPdfStyle }) => {
      try {
        const url = await fetchGoogleFontUrl(
          family,
          weight === 'bold' || weight === '700'
            ? '700'
            : weight === '600' || weight === 600
              ? '600'
              : '400',
          style,
        );
        if (!url) return;
        const buffer = await fetch(url)
          .then((r) => (r.ok ? r.arrayBuffer() : null))
          .catch(() => null);
        if (!buffer) return;
        const b64   = arrayBufferToBase64(buffer);
        const fname = `${family.replace(/ /g, '_')}-${jsPdfStyle}.ttf`;
        pdf.addFileToVFS(fname, b64);
        pdf.addFont(fname, family, jsPdfStyle);
      } catch {
        /* ignore — falls back to Helvetica */
      }
    }),
  );
}

/**
 * Relayout all text inside fixed frames so PDF matches the editor box (wrap + shrink, clip overflow).
 */
export function prepareSceneForPdfExport(scene) {
  clearMeasureCache();
  const cloned = JSON.parse(JSON.stringify(scene));
  const w = Math.max(1, Math.round(Number(cloned.width) || 800));
  const h = Math.max(1, Math.round(Number(cloned.height) || 600));
  cloned.width = w;
  cloned.height = h;
  for (const obj of cloned.objects || []) {
    if (obj.type !== 'text') continue;
    delete obj.layout;
    const base = obj.constraints || migrateFieldConfigToConstraints(obj.fieldConfig);
    obj.constraints = {
      ...base,
      layoutMode: 'fixedFrame',
      overflowStrategy: base.overflowStrategy === 'scale' ? 'scale' : 'wrap',
    };
  }
  clearMeasureCache();
  layoutSceneGraph(cloned, createMeasureFn(true));
  return cloned;
}

/** Only relayout text that has no computed lines yet (import edge cases). */
function ensureTextLayout(scene, measure) {
  if (!scene?.objects) return;
  for (const obj of scene.objects) {
    if (obj.type !== 'text') continue;
    const lines = obj.layout?.lines;
    if (Array.isArray(lines) && lines.length > 0) continue;
    layoutTextNode(obj, measure);
  }
}

/**
 * Inject Google Fonts and load variants used on the Fabric canvas before raster export.
 */
async function loadFontsForFabricCanvas(canvas) {
  const objects = [];
  function collect(obj) {
    if (!obj) return;
    if (obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox') {
      objects.push({
        type: 'text',
        typography: {
          fontFamily: obj.get?.('fontFamily') || obj.fontFamily,
          fontWeight: obj.get?.('fontWeight') || obj.fontWeight || 'normal',
          fontStyle: obj.get?.('fontStyle') || obj.fontStyle || 'normal',
        },
      });
    }
    if (obj.type === 'group' && typeof obj.getObjects === 'function') {
      obj.getObjects().forEach(collect);
    }
  }
  (canvas.getObjects ? canvas.getObjects() : []).forEach(collect);
  await loadSceneFontVariants(objects);
}

/**
 * Fabric canvas → raster PNG → PDF.
 *
 * Injects and awaits every Google Font used in the canvas before calling
 * toDataURL(), so fonts and underlines are rendered exactly as they appear
 * in the editor.  Renders at 3× for print-quality output.
 */
export async function exportCanvasToVectorPdf(clonedCanvas, pageW, pageH, pdfFileName, exportMeta = {}) {
  const w = Math.max(1, Math.round(Number(pageW) || clonedCanvas.getWidth()));
  const h = Math.max(1, Math.round(Number(pageH) || clonedCanvas.getHeight()));
  if (clonedCanvas.getWidth() !== w || clonedCanvas.getHeight() !== h) {
    clonedCanvas.setDimensions({ width: w, height: h });
  }

  // Load every font used in this canvas then re-render before rasterising
  await loadFontsForFabricCanvas(clonedCanvas);
  clonedCanvas.renderAll?.();

  // 3× scale for print quality; keep the longer edge ≤ 9 000 px
  const multiplier = Math.max(1, Math.min(3, Math.floor(9000 / Math.max(w, h, 1))));
  const dataUrl = clonedCanvas.toDataURL({ multiplier, format: 'png' });

  const { jsPdfUnit, pageW: physW, pageH: physH } = getJsPdfPageFormat(w, h, {
    resolution: exportMeta.resolution,
    unit: exportMeta.unit,
  });

  const pdf = new jsPDF({ unit: jsPdfUnit, format: [physW, physH], compress: true });
  pdf.addImage(dataUrl, 'PNG', 0, 0, physW, physH);
  pdf.save(pdfFileName || 'design.pdf');
}

function prepareSceneForVectorExport(scene, options = {}) {
  const preserveLayout = options.preserveLayout !== false;
  clearMeasureCache();
  const cloned = JSON.parse(JSON.stringify(scene));
  const w = Math.max(1, Math.round(Number(cloned.width) || 800));
  const h = Math.max(1, Math.round(Number(cloned.height) || 600));
  cloned.width = w;
  cloned.height = h;
  const measure = createMeasureFn(true);
  if (!preserveLayout) {
    for (const obj of cloned.objects || []) {
      if (obj.type !== 'text') continue;
      delete obj.layout;
      const base = obj.constraints || migrateFieldConfigToConstraints(obj.fieldConfig);
      obj.constraints = { ...base, layoutMode: 'fixedFrame', overflowStrategy: 'clip' };
    }
    layoutSceneGraph(cloned, measure);
  } else {
    for (const obj of cloned.objects || []) {
      if (obj.type !== 'text') continue;
      const lines = obj.layout?.lines;
      const hasLines = Array.isArray(lines) && lines.length > 0 && lines.join('').trim().length > 0;
      const hasText = String(obj.text ?? '').trim().length > 0;
      if (!hasLines && hasText) delete obj.layout;
    }
    ensureTextLayout(cloned, measure);
  }
  return cloned;
}

/**
 * Render one prepared scene onto an existing jsPDF page (vector SVG — fonts + underlines).
 */
export async function appendSceneToVectorPdf(pdf, scene, background, exportMeta = {}) {
  const cloned = prepareSceneForVectorExport(scene, { preserveLayout: true });
  const w = cloned.width;
  const h = cloned.height;
  const svgEl = renderSceneToSvgElement(cloned, background);
  const { pageW: physW, pageH: physH } = getJsPdfPageFormat(w, h, {
    resolution: exportMeta.resolution,
    unit: exportMeta.unit,
  });

  await embedFontsInPdf(pdf, cloned);

  await svg2pdf(svgEl, pdf, {
    xOffset: 0,
    yOffset: 0,
    width: physW,
    height: physH,
  });

  return { scene: cloned, svg: renderSceneToSvgString(cloned, background) };
}

/**
 * Scene graph → vector PDF (fallback). Default preserves editor layout lines — no reflow.
 * @param {object} [options]
 * @param {boolean} [options.preserveLayout=true] use layout.lines / typography from editor as-is
 */
export async function exportSceneToVectorPdf(scene, pdfFileName, exportMeta = {}, options = {}) {
  const cloned = prepareSceneForVectorExport(scene, options);
  const w = cloned.width;
  const h = cloned.height;
  const background = options.background ?? null;
  const { jsPdfUnit, pageW: physW, pageH: physH } = getJsPdfPageFormat(w, h, {
    resolution: exportMeta.resolution,
    unit: exportMeta.unit,
  });

  const pdf = new jsPDF({
    unit: jsPdfUnit,
    format: [physW, physH],
    compress: true,
  });

  const result = await appendSceneToVectorPdf(pdf, cloned, background, exportMeta);
  pdf.save(pdfFileName || 'design.pdf');
  return result;
}
